import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ChevronDown, ChevronUp } from 'lucide-react';
import './ETPOS/CommonStyles.css';
import './ETPOS/AddSellStyles.css';
import './CardForm.css';
import { permissionsData } from '../data/permissionsData';

const AddRole = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        permissions: [] // Array of selected permission strings
    });

    // Define sections for grouping permissions
    const permissionSections = [
        {
            title: "User Management",
            categories: ["User", "Roles", "Supplier", "Customer", "Crm"]
        },
        {
            title: "Products",
            categories: ["Product", "Brand", "Tax rate", "Unit", "Category", "Manufacturing", "Access selling price groups", "Repair"]
        },
        {
            title: "Purchases",
            categories: ["Purchase & Stock Adjustment", "Purchase Requisition", "Purchase Order"]
        },
        {
            title: "Sells",
            categories: ["POS", "Sell", "Sales Order", "Draft", "Quotation", "Shipments"]
        },
        {
            title: "Finance & Accounts",
            categories: ["Expense", "Account", "Accounting", "Cash Register"]
        },
        {
            title: "Reports",
            categories: ["Report"]
        },
        {
            title: "Settings & Others",
            categories: ["Settings", "Business Settings", "Essentials", "Project", "Superadmin", "Woocommerce", "AiAssistance", "Other", "Home"]
        }
    ];

    // Initialize only the first section as expanded
    const [expandedSections, setExpandedSections] = useState({
        "User Management": true
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const togglePermission = (permission) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission];
            return { ...prev, permissions: newPermissions };
        });
    };

    const toggleCategory = (categoryItems) => {
        setFormData(prev => {
            const allSelected = categoryItems.every(item => prev.permissions.includes(item));
            let newPermissions = [...prev.permissions];

            if (allSelected) {
                // Deselect all
                newPermissions = newPermissions.filter(p => !categoryItems.includes(p));
            } else {
                // Select all (add missing ones)
                categoryItems.forEach(item => {
                    if (!newPermissions.includes(item)) {
                        newPermissions.push(item);
                    }
                });
            }
            return { ...prev, permissions: newPermissions };
        });
    };

    const toggleSection = (sectionTitle) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionTitle]: !prev[sectionTitle]
        }));
    };

    const toggleAllSections = (expand) => {
        const newExpandedState = {};
        permissionSections.forEach(section => {
            newExpandedState[section.title] = expand;
        });
        setExpandedSections(newExpandedState);
    };

    // Helper to get all items in a section
    const getSectionItems = (sectionCategories) => {
        const items = [];
        sectionCategories.forEach(cat => {
            const data = permissionsData.find(p => p.category === cat);
            if (data) items.push(...data.items);
        });
        return items;
    };

    const toggleSectionAll = (sectionCategories) => {
        const allItems = getSectionItems(sectionCategories);
        setFormData(prev => {
            const allSelected = allItems.every(item => prev.permissions.includes(item));
            let newPermissions = [...prev.permissions];

            if (allSelected) {
                newPermissions = newPermissions.filter(p => !allItems.includes(p));
            } else {
                allItems.forEach(item => {
                    if (!newPermissions.includes(item)) newPermissions.push(item);
                });
            }
            return { ...prev, permissions: newPermissions };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Role added:', formData);
        navigate('/users', { state: { activeTab: 'roles' } });
    };

    return (
        <div className="card-form-container">
            {/* Header */}
            <div className="page-header" style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button
                    onClick={() => navigate('/users', { state: { activeTab: 'roles' } })}
                    style={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '40px',
                        height: '40px'
                    }}
                    title="Back"
                >
                    ←
                </button>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>Add Role</h3>
            </div>

            <form id="roleForm" onSubmit={handleSubmit} className="sell-form" style={{ gap: '0.5rem' }}>
                <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
                    <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
                        <div className="form-group">
                            <label>Role Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section" style={{ padding: '1.5rem', marginBottom: '0' }}>
                    <div className="permissions-top-header">
                        <h3>Permissions</h3>
                        <div className="header-buttons">
                            <button type="button" onClick={() => toggleAllSections(true)} className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.85rem' }}>Expand All</button>
                            <button type="button" onClick={() => toggleAllSections(false)} className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.85rem' }}>Collapse All</button>
                        </div>
                    </div>

                    {permissionSections.map((section, index) => {
                        const sectionItems = getSectionItems(section.categories);
                        const isSectionAllSelected = sectionItems.length > 0 && sectionItems.every(item => formData.permissions.includes(item));
                        const isExpanded = expandedSections[section.title];

                        return (
                            <div key={index} style={{ marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                <div
                                    className="permissions-section-header"
                                    style={{ borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}
                                    onClick={() => toggleSection(section.title)}
                                >
                                    <div className="section-title">
                                        {isExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
                                        <h4>{section.title}</h4>
                                    </div>
                                    <label className="select-all-label" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={isSectionAllSelected}
                                            onChange={() => toggleSectionAll(section.categories)}
                                        />
                                        Select all
                                    </label>
                                </div>

                                {isExpanded && (
                                    <div className="permissions-section-body">
                                        {section.categories.map((catName, catIndex) => {
                                            const categoryData = permissionsData.find(p => p.category === catName);
                                            if (!categoryData) return null;
                                            const isCatAllSelected = categoryData.items.every(item => formData.permissions.includes(item));
                                            return (
                                                <div key={catIndex} style={{ marginBottom: '1rem' }}>
                                                    <div className="permissions-category-header">
                                                        <h5>{catName}</h5>
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer', margin: 0, color: '#64748b' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isCatAllSelected}
                                                                onChange={() => toggleCategory(categoryData.items)}
                                                                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                                                            />
                                                            Select all
                                                        </label>
                                                    </div>
                                                    <div className="permissions-grid">
                                                        {categoryData.items.map((item, i) => (
                                                            <label key={i} className="permission-item" onClick={() => togglePermission(item)}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.permissions.includes(item)}
                                                                    onChange={() => togglePermission(item)}
                                                                />
                                                                <span>{item}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="action-buttons-section" style={{ margin: '1rem', padding: '0 0.5rem' }}>
                    <div className="action-buttons" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                        <button
                            type="submit"
                            className="save-btn"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1.25rem',
                                backgroundColor: '#3b82f6',
                                border: 'none',
                                borderRadius: '6px',
                                color: 'white',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                cursor: 'pointer'
                            }}
                        >
                            <Save size={16} /> Save Role
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AddRole;
